import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { ContentService } from "../../services/content.service";
import { ProfileInfo } from "../../models/donorProfile.model";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EsignatureModalComponent } from '../../shared/esignature-modal/esignature-modal.component';
import { Contract } from '../../models/contract.model';
import { LocaleService } from "../../services/locale.service";
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';

type StepState = 'default' | 'active' | 'completed';

type PostComprehensiveCopy = {
  title: string;
  highlightedText: string;
  description: string;
  buttonText: string;
  consentSignedMessage: string;
  redirectingMessage: string;
  signingUrlError: string;
  signatureNotDetectedError: string;
  contractStatusError: string;
  missingNameError: string;
  missingNameTitle: string;
  missingNameFirstNameLabel: string;
  missingNameLastNameLabel: string;
  missingNameCancelButton: string;
  missingNameSaveButton: string;
  missingNameRequiredError: string;
  missingNameSaveError: string;
  steps: { label: string; state: StepState }[];
};

const postComprehensiveCopy: Record<string, PostComprehensiveCopy> = {
  en: {
    title: 'Congratulations, you have taken an ',
    highlightedText: 'important step!',
    description:
      'Now, before moving forward, we need you to review and sign the informed consent. Once you sign, we will begin reviewing your information, and within 5 business days, our team will contact you to coordinate appointments.',
    buttonText: 'Review and Sign Consent',
    consentSignedMessage: 'Consent signed successfully!',
    redirectingMessage: 'Redirecting to your journey...',
    signingUrlError: 'Could not retrieve signing URL.',
    signatureNotDetectedError:
      'Signature not detected yet. Please refresh or try again.',
    contractStatusError: 'Error checking contract status.',
    missingNameError:
      'Your first and last name are required before you can sign consent.',
    missingNameTitle: 'Name required',
    missingNameFirstNameLabel: 'First name',
    missingNameLastNameLabel: 'Last name',
    missingNameCancelButton: 'Cancel',
    missingNameSaveButton: 'Save and continue',
    missingNameRequiredError: 'Please enter both first and last name.',
    missingNameSaveError: 'Unable to save your name. Please try again.',
    steps: [
      { label: 'Initial Questionnaire', state: 'completed' },
      { label: 'Comprehensive Questionnaire', state: 'completed' },
      { label: 'Living Kidney Donor Consent', state: 'default' },
      { label: 'Consultation & Evaluation', state: 'default' },
    ],
  },
  es: {
    title: 'Felicitaciones, ha dado un ',
    highlightedText: 'paso importante.',
    description:
      'Ahora, antes de continuar, necesitamos que revise y firme el consentimiento informado. Una vez que lo firme, comenzaremos a revisar su información y, dentro de 5 días hábiles, nuestro equipo se comunicará con usted para coordinar las citas.',
    buttonText: 'Firmar consentimiento',
    consentSignedMessage: 'Consentimiento firmado correctamente.',
    redirectingMessage: 'Redirigiendo a su recorrido...',
    signingUrlError: 'No se pudo obtener el enlace de firma.',
    signatureNotDetectedError:
      'Aún no se detecta la firma. Actualice la página o inténtelo de nuevo.',
    contractStatusError: 'Error al verificar el estado del contrato.',
    missingNameError:
      'Su nombre y apellido son obligatorios antes de que pueda firmar el consentimiento.',
    missingNameTitle: 'Nombre requerido',
    missingNameFirstNameLabel: 'Nombre',
    missingNameLastNameLabel: 'Apellido',
    missingNameCancelButton: 'Cancelar',
    missingNameSaveButton: 'Guardar y continuar',
    missingNameRequiredError: 'Ingrese su nombre y apellido.',
    missingNameSaveError: 'No se pudo guardar su nombre. Inténtelo de nuevo.',
    steps: [
      { label: 'Cuestionario Inicial', state: 'completed' },
      { label: 'Cuestionario Completo', state: 'completed' },
      { label: 'Consentimiento para Donante Vivo de Riñón', state: 'default' },
      { label: 'Consulta y Evaluación', state: 'default' },
    ],
  },
};

@Component({
  selector: "app-post-comprehensive-screen",
  templateUrl: "./post-comprehensive-screen.component.html",
  styleUrl: "./post-comprehensive-screen.component.scss",
})
export class PostComprehensiveScreenComponent {
  loading = false;
  showSuccess = false;
  polling = false;
  pollAttempts = 0;
  maxPollAttempts = 10;

  get copy() {
    return (
      postComprehensiveCopy[
        this.localeService.currentLocale as keyof typeof postComprehensiveCopy
      ] || postComprehensiveCopy['en']
    );
  }

  constructor(
    private contentService: ContentService,
    private router: Router,
    private modalService: NgbModal,
    private localeService: LocaleService
  ) {}

  handleButtonClick() {
    this.loading = true;
    this.contentService.getDonorProfile().subscribe({
      next: (profileInfo: ProfileInfo) => {
        const fullName = [
          profileInfo?.data.donor.firstName,
          profileInfo?.data.donor.lastName,
        ]
          .filter((name) => !!name && name !== "null")
          .join(" ")
          .trim();

        if (!fullName) {
          this.loading = false;
          this.showMissingNameModal(profileInfo);
          return;
        }

        this.initiateConsent(fullName);
      },
      error: (error) => {
        console.error("Error fetching donor profile:", error);
        this.loading = false;
      }
    });
  }

  private initiateConsent(fullName: string): void {
    this.loading = true;
    this.contentService.initiateConsentRequest(fullName).subscribe({
          next: (response: any) => {
            // Assume response contains { signingUrl: string }
            const signingUrl = response?.signingUrl || response?.url || response?.embeddedSigningUrl;
            if (signingUrl) {
              const modalRef = this.modalService.open(EsignatureModalComponent, {
                centered: true,
                size: 'xl',
                backdrop: 'static',
              });
              modalRef.componentInstance.signingUrl = signingUrl;
              modalRef.result.then((result) => {
                if (result === 'signed') {
                  this.showSuccess = true;
                  this.pollForSignedContract();
                }
              }).catch((error) => {
                // Handle modal dismissal or errors gracefully
                console.warn('Modal closed or error:', error);
              }).finally(() => {
                this.loading = false;
                this.router.navigate(['/journey']);
              });
            } else {
              this.loading = false;
              alert(this.copy.signingUrlError);
            }
          },
          error: (error) => {
            console.error("Error initiating consent request:", error);
            this.loading = false;
          },
        });
  }

  private showMissingNameModal(profileInfo: ProfileInfo): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.title = this.copy.missingNameTitle;
    modalRef.componentInstance.message = this.copy.missingNameError;
    modalRef.componentInstance.actionType = 'profileName';
    modalRef.componentInstance.firstName = profileInfo?.data.donor.firstName || '';
    modalRef.componentInstance.lastName = profileInfo?.data.donor.lastName || '';
    modalRef.componentInstance.firstNameLabel = this.copy.missingNameFirstNameLabel;
    modalRef.componentInstance.lastNameLabel = this.copy.missingNameLastNameLabel;
    modalRef.componentInstance.cancelButtonText = this.copy.missingNameCancelButton;
    modalRef.componentInstance.confirmButtonText = this.copy.missingNameSaveButton;
    modalRef.componentInstance.requiredErrorText = this.copy.missingNameRequiredError;

    modalRef.result
      .then((result) => {
        if (result?.action !== 'profileName') {
          return;
        }

        this.loading = true;
        this.contentService.updateDonorProfilePartial({
          firstName: result.firstName,
          lastName: result.lastName,
        }).subscribe({
          next: () => {
            this.initiateConsent(`${result.firstName} ${result.lastName}`.trim());
          },
          error: (error) => {
            console.error('Error saving donor name:', error);
            this.loading = false;
            alert(this.copy.missingNameSaveError);
          },
        });
      })
      .catch(() => {});
  }

  pollForSignedContract() {
    console.log('Polling for signed contract started');
    this.polling = true;
    this.pollAttempts = 0;
    console.log('Polling for signed contract started');
    const poll = () => {
      this.contentService.getContract().subscribe({
        next: (contract: Contract) => {
          console.log('contract', contract);
          if (contract && contract.status === 'contract-signed') {
            console.log('Contract signed detected. Navigating to /journey');
            this.polling = false;
            this.showSuccess = false;
            this.router.navigate(['/journey']);
          } else if (this.pollAttempts < this.maxPollAttempts) {
            this.pollAttempts++;
            setTimeout(poll, 2000); // Poll every 2 seconds
          } else {
            this.polling = false;
            // Optionally show a message or allow retry
            alert(this.copy.signatureNotDetectedError);
            this.showSuccess = false;
            console.log('Polling ended: max attempts reached');
          }
        },
        error: () => {
          this.polling = false;
          alert(this.copy.contractStatusError);
          this.showSuccess = false;
          console.log('Polling ended: error occurred');
        }
      });
    };
    poll();
  }
}
